import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useState, useEffect } from "react";
import { InputLabel, Grid, Card } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import Footer from "examples/Footer";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import { useParams, useNavigate } from "react-router-dom";
import axios, { baseURL } from "utils/api";
import { toast } from "react-toastify";
import PermissionTable from "components/PermissionTable/PermissionTable";
import { getAllPermissions } from "utils/permissionConstants";

function EditAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
  });

  // Permissions state - now an object with CRUD operations
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [selectAllModules, setSelectAllModules] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Handle permission changes
  const handlePermissionChange = (newPermissions) => {
    setSelectedPermissions(newPermissions);

    // Check if all modules are selected
    const allPerms = getAllPermissions();
    const allSelected = JSON.stringify(newPermissions) === JSON.stringify(allPerms);
    setSelectAllModules(allSelected);
  };

  // Handle select all modules
  const handleSelectAllModules = () => {
    if (selectAllModules) {
      // Deselect all
      setSelectedPermissions({});
      setSelectAllModules(false);
    } else {
      // Select all
      const allPerms = getAllPermissions();
      setSelectedPermissions(allPerms);
      setSelectAllModules(true);
    }
  };

  // Input handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // GET API to pre-fill form
  useEffect(() => {
    axios
      .get(`${baseURL}/user/${id}`)
      .then((res) => {
        console.log("Fetched admin data:", res.data);
        if (res.data.success) {
          setFormData({
            fullName: res.data.data.fullName,
            email: res.data.data.email,
          });
          // Set permissions - now expecting object format
          const permissions = res.data.data.permissions || {};
          setSelectedPermissions(permissions);

          // Check if all permissions are selected
          const allPerms = getAllPermissions();
          const allSelected = JSON.stringify(permissions) === JSON.stringify(allPerms);
          setSelectAllModules(allSelected);

          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("❌ Error fetching admin:", err);
        toast.error("Failed to load sub-admin data");
        setLoading(false);
      });
  }, [id]);

  // PUT API to update
  const handleSubmit = (e) => {
    e.preventDefault();

    // Check if at least one permission is selected
    const hasPermissions = Object.values(selectedPermissions).some((modulePerms) =>
      Object.values(modulePerms).some((val) => val === true)
    );

    if (!hasPermissions) {
      toast.error("Please assign at least one permission!");
      return;
    }

    setUpdateLoading(true);

    axios
      .put(`${baseURL}/user/${id}`, {
        fullName: formData.fullName,
        email: formData.email,
        permissions: selectedPermissions,
      })
      .then((res) => {
        console.log("✅ Admin updated:", res.data);
        if (res.data.success) {
          toast.success(res.data.message || "Sub-Admin updated successfully!");

          // Check if the current user updated their own permissions
          const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
          if (currentUser._id === id || currentUser.id === id) {
            console.log("Current user updated their own permissions, refreshing...");
            // Refresh permissions from server
            if (window.refreshPermissions) {
              window.refreshPermissions();
            }
          }
          navigate("/subadmin");
        } else {
          toast.error(res.data.message || "Failed to update Sub-Admin");
        }
        setUpdateLoading(false);
      })
      .catch((err) => {
        console.error("❌ Error updating admin:", err);
        if (err.response && err.response.data) {
          toast.error(err.response.data.error || "Failed to update Sub-Admin");
        } else {
          toast.error("Something went wrong. Please try again!");
        }
        setUpdateLoading(false);
      });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card sx={{ boxShadow: "0 8px 30px rgba(0,0,0,0.1)", borderRadius: "20px" }}>
            <ArgonBox p={4}>
              <ArgonBox mb={4} textAlign="center">
                <ArgonTypography variant="h4" fontWeight="bold" mb={1}>
                  Edit Sub-Admin
                </ArgonTypography>
                <ArgonTypography variant="body2">Update details and permissions</ArgonTypography>
              </ArgonBox>

              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="body2">Loading...</ArgonTypography>
                </ArgonBox>
              ) : (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Full Name */}
                    <Grid item xs={12} md={6}>
                      <InputLabel sx={{ mb: 1, display: "flex", alignItems: "center" }}>
                        <PersonIcon color="primary" sx={{ fontSize: 18, mr: 1 }} />
                        <ArgonTypography variant="body2" fontWeight="medium">
                          Full Name
                        </ArgonTypography>
                      </InputLabel>
                      <ArgonInput
                        fullWidth
                        name="fullName"
                        placeholder="Enter full name"
                        value={formData.fullName}
                        onChange={handleChange}
                      />
                    </Grid>

                    {/* Email */}
                    <Grid item xs={12} md={6}>
                      <InputLabel sx={{ mb: 1, display: "flex", alignItems: "center" }}>
                        <EmailIcon color="primary" sx={{ fontSize: 18, mr: 1 }} />
                        <ArgonTypography variant="body2" fontWeight="medium">
                          Email Address
                        </ArgonTypography>
                      </InputLabel>
                      <ArgonInput
                        fullWidth
                        name="email"
                        type="email"
                        placeholder="Enter email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>

                  {/* Permissions Section */}
                  <PermissionTable
                    selectedPermissions={selectedPermissions}
                    onPermissionChange={handlePermissionChange}
                    selectAllModules={selectAllModules}
                    onSelectAllModules={handleSelectAllModules}
                  />

                  {/* Buttons */}
                  <ArgonBox display="flex" justifyContent="flex-end" mt={3} gap={2}>
                    <ArgonButton
                      variant="outlined"
                      color="primary"
                      onClick={() => navigate("/subadmin")}
                    >
                      Cancel
                    </ArgonButton>
                    <ArgonButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={updateLoading}
                    >
                      {updateLoading ? "Updating..." : "Update"}
                    </ArgonButton>
                  </ArgonBox>
                </form>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditAdmin;

// import ArgonTypography from 'components/ArgonTypography'
// import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
// import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
// import React from 'react'

// import ArgonBox from "components/ArgonBox";

// import Card from "@mui/material/Card";

// // Argon Dashboard 2 MUI components

// // Argon Dashboard 2 MUI examples
// import Footer from "examples/Footer";

// import breakpoints from "assets/theme/base/breakpoints";
// import { Button, InputLabel, Box, Avatar } from "@mui/material";
// import PersonIcon from '@mui/icons-material/Person';
// import EmailIcon from '@mui/icons-material/Email';
// import PhoneIcon from '@mui/icons-material/Phone';
// import WorkIcon from '@mui/icons-material/Work';
// import LockIcon from '@mui/icons-material/Lock';
// import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
// import { Link } from "react-router-dom";

// import { useState,useEffect } from "react";

// import {Grid } from "@mui/material";
// import ArgonInput from 'components/ArgonInput';
// import ArgonButton from 'components/ArgonButton';
// import {
//   Table,
//   TableBody,
//   TableCell,
//   TableRow,
//   Checkbox,
//   IconButton,
// } from "@mui/material";
// import { ExpandMore } from "@mui/icons-material";

// function AddTutor() {

//     const [formData, setFormData] = useState({
//     name: "",
//     phone: "",
//     email: "",
//     designation: "",
//     password: "",
//     profileImage: null,
//   });

//   const handleChange = (e) => {
//     const { name, value, files } = e.target;
//     setFormData({
//       ...formData,
//       [name]: files ? files[0] : value,
//     });
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     console.log("Form Data:", formData);
//   };

//   //profile image show k liye code
//     const [preview, setPreview] = useState(null);

//     const handleChange1 = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       // File ko URL me convert karke state me store karo
//       setPreview(URL.createObjectURL(file));
//     }
//   };

//      const permissions = [
//     "Video",
//     "Test user",
//     "Contact List",
//     "Banner",
//     "Blogs",
//     "Student Leader Board",
//     "Tutor Leader Board",
//     "Dashboard",
//     "Report",
//   ];

//   // Sections with dropdown children
//   const sections = [
//     {
//       title: "People",
//       children: ["Tutors", "Parents", "Subadmin"],
//     },
//     {
//       title: "Activities",
//       children: ["Assignments", "Tests", "Projects"],
//     },
//     {
//       title: "Communication",
//       children: ["Chat", "Emails", "Notifications"],
//     },
//     {
//       title: "Finance Management",
//       children: [
//         "Raise Invoice",
//         "Draft Tutor Salary",
//         "All Transaction",
//         "Client Balance",
//       ],
//     },
//     {
//       title: "Useful Corners",
//       children: ["Resources", "Library"],
//     },
//     {
//       title: "Enquiry",
//       children: ["Student Enquiry", "Tutor Enquiry"],
//     },
//   ];

//   // State for expand/collapse
//   const [openSections, setOpenSections] = useState({});
//   const toggleSection = (title) => {
//     setOpenSections((prev) => ({
//       ...prev,
//       [title]: !prev[title],
//     }));
//   };
//   return (
//    <DashboardLayout>
//       <DashboardNavbar />
//       <ArgonBox py={3}>
//         <ArgonBox mb={3}>
//           <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: '16px' }}>
//             <ArgonBox p={4}>
//               {/* Header */}
//               <ArgonBox mb={4} textAlign="center">
//                 <ArgonTypography variant="h4" fontWeight="bold" color="primary" mb={1}>
//                   Edit SubAdmin
//                 </ArgonTypography>
//                 <ArgonTypography variant="body2" color="text" opacity={0.7}>
//                   Fill in the details to edit Sub-Admin account
//                 </ArgonTypography>
//               </ArgonBox>

//               {/* FORM START */}
//               <form onSubmit={handleSubmit}>
//                 <Grid container spacing={3}>

//                   {/* Name */}
//                   <Grid item xs={12} md={6}>
//                     <InputLabel display="flex" sx={{ mb: 1, fontWeight: 'medium', color: 'text.primary' }}>
//                       <PersonIcon color='primary' sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
//                       <ArgonTypography variant="caption" fontWeight="medium" color="text" fontSize="16px">Full Name</ArgonTypography>
//                     </InputLabel>
//                     <ArgonInput
//                       fullWidth
//                       name="name"
//                       placeholder="Enter full name"
//                       value={formData.name}
//                       onChange={handleChange}
//                       variant="outlined"

//                     />
//                   </Grid>

//                   {/* Email */}
//                   <Grid item xs={12} md={6}>
//                     <InputLabel sx={{ mb: 1, fontWeight: 'medium', color: 'text.primary' }}>
//                       <EmailIcon color='primary' sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
//                       <ArgonTypography variant="caption" fontWeight="medium" color="text" fontSize="16px">Email Address</ArgonTypography>
//                     </InputLabel>
//                     <ArgonInput
//                       fullWidth
//                       name="email"
//                       type="email"
//                       placeholder="Enter email address"
//                       value={formData.email}
//                       onChange={handleChange}
//                       variant="outlined"
//                     />
//                   </Grid>

//                   {/* Password */}
//                   <Grid item xs={12} md={6}>
//                     <InputLabel sx={{ mb: 1, fontWeight: 'medium', color: 'text.primary' }}>
//                       <LockIcon color='primary' sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
//                       <ArgonTypography variant="caption" fontWeight="medium" color="text" fontSize="16px">Password</ArgonTypography>
//                     </InputLabel>
//                     <ArgonInput
//                       fullWidth
//                       name="password"
//                       type="password"
//                       placeholder="Enter password"
//                       value={formData.password}
//                       onChange={handleChange}
//                       variant="outlined"
//                     />
//                   </Grid>
//                 </Grid>

//                 {/* Submit Buttons */}
//                 <ArgonBox display="flex" justifyContent="center" gap={2} mt={4}>
//                   <ArgonButton
//                     variant="outlined"
//                     component={Link}
//                     to="/subadmin"
//                     color="primary"
//                   >
//                     Cancel
//                   </ArgonButton>
//                   <ArgonButton
//                     type="submit"
//                     variant="contained"
//                     color="primary"
//                   >
//                     Submit
//                   </ArgonButton>
//                 </ArgonBox>
//               </form>
//               {/* FORM END */}
//             </ArgonBox>
//           </Card>
//         </ArgonBox>
//       </ArgonBox>

//               <ArgonBox
//       sx={{
//         borderRadius: 3,
//         background: "linear-gradient(145deg, #ffffff, #f9f9ff)",
//         width: "100%",
//         height: "100%",
//         p: 2,
//         boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
//         transition: "all 0.3s ease",
//         "&:hover": {
//           boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
//         },
//       }}
//     >
//       {/* Header */}
//       <ArgonBox
//         display="flex"
//         justifyContent="space-between"
//         alignItems="center"
//         sx={{
//           height: 60,
//           borderBottom: "2px solid #e0e0e0",
//           px: 3,
//           mb: 1,
//         }}
//       >
//         <ArgonTypography
//           variant="h6"
//           fontWeight="bold"
//           sx={{ color: "#080278" }}
//         >
//           Permissions
//         </ArgonTypography>
//         <ArgonTypography variant="body2" fontWeight="bold" color="text.secondary">
//           Is Allowed
//         </ArgonTypography>
//       </ArgonBox>

//       {/* Table */}
//       <ArgonBox p={2}>
//         <form>
//           <ArgonBox
//             sx={{
//               maxHeight: 280,
//               overflowY: "auto",
//               "&::-webkit-scrollbar": {
//                 width: "6px",
//               },
//               "&::-webkit-scrollbar-thumb": {
//                 background: "#c1c1c1",
//                 borderRadius: "6px",
//               },
//             }}
//           >
//             <Table>
//               <TableBody>
//                 {/* Static Permissions */}
//                 {permissions.map((perm, idx) => (
//                   <TableRow
//                     key={idx}
//                     sx={{
//                       "&:hover": { backgroundColor: "#f5f7ff" },
//                       transition: "0.2s",
//                     }}
//                   >
//                     <TableCell sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#080278" }}>
//                       {perm}
//                     </TableCell>

//                     <TableCell>
//                       <Checkbox name="permissions[]" value={perm} />
//                     </TableCell>
//                   </TableRow>
//                 ))}

//                 {/* Sections with dropdown */}
//                 {sections.map((section, idx) => (
//                   <React.Fragment key={`section-${idx}`}>
//                     <TableRow
//                       sx={{
//                         backgroundColor: "#f1f4ff",
//                       }}
//                     >
//                       <TableCell colSpan={2}>
//                         <ArgonBox display="flex" alignItems="center">
//                           <ArgonTypography
//                             variant="body2"
//                             fontWeight="bold"
//                             fontSize="15px"
//                             sx={{ color: "#080278" }}
//                           >
//                             {section.title}
//                           </ArgonTypography>
//                           <IconButton
//                             size="small"
//                             onClick={() => toggleSection(section.title)}
//                             sx={{
//                               color: "#080278",
//                               ml: 1.5,
//                               transition: "0.2s",
//                               "&:hover": { transform: "scale(1.2)" },
//                             }}
//                           >
//                             <ExpandMore
//                               sx={{
//                                 transform: openSections[section.title]
//                                   ? "rotate(180deg)"
//                                   : "rotate(0deg)",
//                                 transition: "0.3s",
//                               }}
//                             />
//                           </IconButton>
//                         </ArgonBox>
//                       </TableCell>
//                     </TableRow>

//                     {/* Child Rows */}
//                     {openSections[section.title] &&
//                       section.children.map((child, cidx) => (
//                         <TableRow
//                           key={`child-${idx}-${cidx}`}
//                           sx={{
//                             "&:hover": { backgroundColor: "#f9f9ff" },
//                             transition: "0.2s",
//                           }}
//                         >
//                           <TableCell sx={{ pl: 6, fontSize: "0.9rem",fontWeight: 600, color: "#080278" }}>
//                             {child}
//                           </TableCell>
//                           <TableCell>
//                             <Checkbox  name="permissions[]" value={child} />
//                           </TableCell>
//                         </TableRow>
//                       ))}
//                   </React.Fragment>
//                 ))}
//               </TableBody>
//             </Table>
//           </ArgonBox>

//           {/* Actions */}
//           <ArgonBox
//             display="flex"
//             justifyContent="flex-end"
//             mt={3}
//             gap={2}
//           >
//             <ArgonButton
//               variant="outlined"
//               color="primary"
//             >
//               Cancel
//             </ArgonButton>
//             <ArgonButton
//               type="submit"
//               variant="contained"
//               color="primary"
//             >
//               Save
//             </ArgonButton>
//           </ArgonBox>
//         </form>
//       </ArgonBox>
//            </ArgonBox>

//       <Footer />
//     </DashboardLayout>

//   )
// }

// export default AddTutor
